import { DateTime } from "luxon"

// ── CHI HA TOCCATO LA LEZIONE ─────────────────────────────────────
// Una lezione la toccano in tanti: il suo maestro, chi gestisce il circolo,
// gli assistenti che segnano le presenze. Aprendola si dice chi altro ci ha
// messo le mani, e solo quando dice qualcosa: chi l'ha inserita, se non è
// il suo maestro, e chi l'ha modificata per ultimo, se non è lui. Una
// lezione che il suo maestro si è messo e corretto da solo non dice niente,
// come quelle di prima che le lezioni portassero la firma.

const toMillis = value => value?.toMillis?.() ?? null

// `insertedBy` e `lastEdit.coachID` sono id di maestri: il nome lo mette
// chi li mostra. Una lezione appena creata è firmata nello stesso istante in
// cui nasce (`addLesson`): quella firma è l'inserimento, non una modifica.
export const lessonAuthorship = lessonData => {
	const coachID = lessonData?.coachID ?? ""
	const createdBy = lessonData?.createdBy || ""
	const updatedBy = lessonData?.updatedBy || ""
	const updatedAt = lessonData?.updatedAt

	const createdMillis = toMillis(lessonData?.createdAt)
	const neverEdited = createdMillis !== null && createdMillis === toMillis(updatedAt)

	return {
		insertedBy: createdBy && createdBy !== coachID ? createdBy : null,
		lastEdit:
			updatedBy && updatedBy !== coachID && !neverEdited && updatedAt?.toDate
				? { coachID: updatedBy, at: updatedAt.toDate() }
				: null,
	}
}

// "12/10 alle 18:30", e l'anno solo se non è questo
export const formatEditTime = (date, now = new Date()) => {
	const when = DateTime.fromJSDate(date)
	const sameYear = when.year === DateTime.fromJSDate(now).year
	return when.toFormat(sameYear ? "dd/LL 'alle' HH:mm" : "dd/LL/yyyy 'alle' HH:mm")
}

// Le righe da mostrare in fondo alla lezione. `nameOf` dà il nome di un
// maestro; chi guarda è "te".
export const lessonAuthorshipLines = (lessonData, { myCoachID, nameOf, now } = {}) => {
	const { insertedBy, lastEdit } = lessonAuthorship(lessonData)
	const lines = []

	if (insertedBy)
		lines.push(insertedBy === myCoachID ? "Inserita da te" : `Inserita da ${nameOf(insertedBy)}`)

	if (lastEdit) {
		const when = formatEditTime(lastEdit.at, now)
		lines.push(
			lastEdit.coachID === myCoachID
				? `Ultima modifica tua, ${when}`
				: `Ultima modifica: ${nameOf(lastEdit.coachID)}, ${when}`,
		)
	}

	return lines
}

// ── LEZIONI ELIMINATE ──────────────────────────────────────────────
// Una serie eliminata ("anche le prossime") lascia una riga per lezione, e
// tutte nello stesso momento: nell'elenco diventano una voce sola. Si
// uniscono le righe vicine della stessa serie, eliminate dalla stessa persona
// a pochi minuti l'una dall'altra; una lezione della serie tolta un altro
// giorno resta una voce a sé.
const SERIES_DELETION_WINDOW = 10 * 60 * 1000

// `deletions` arriva dalla più recente: le voci restano in quell'ordine, e
// le lezioni di una serie si mettono in ordine di data.
export const groupLessonDeletions = deletions => {
	const groups = []

	deletions.forEach(deletion => {
		const deletedAt = toMillis(deletion.deletedAt) ?? 0
		const last = groups[groups.length - 1]
		if (
			deletion.recurrencyID &&
			last?.recurrencyID === deletion.recurrencyID &&
			last.deletedBy === (deletion.deletedBy || "") &&
			Math.abs(last.deletedAtMillis - deletedAt) <= SERIES_DELETION_WINDOW
		) {
			last.lessons.push(deletion)
			return
		}
		groups.push({
			key: deletion.lessonID,
			recurrencyID: deletion.recurrencyID || "",
			deletedBy: deletion.deletedBy || "",
			deletedAt: deletion.deletedAt,
			deletedAtMillis: deletedAt,
			lessons: [deletion],
		})
	})

	groups.forEach(group =>
		group.lessons.sort((a, b) => (toMillis(a.lessonStart) ?? 0) - (toMillis(b.lessonStart) ?? 0)),
	)
	return groups
}

// "lun 12 ott, 18:00 - 19:00" per una lezione, "Serie del lunedì 18:00, 12
// lezioni" per una serie
export const deletionTitle = group => {
	const first = group.lessons[0]
	const start = first?.lessonStart?.toDate ? DateTime.fromJSDate(first.lessonStart.toDate()) : null
	const end = first?.lessonEnd?.toDate ? DateTime.fromJSDate(first.lessonEnd.toDate()) : null
	if (!start) return group.lessons.length > 1 ? `${group.lessons.length} lezioni` : "Lezione"

	if (group.lessons.length > 1)
		return `Serie del ${start.setLocale("it").toFormat("cccc HH:mm")}, ${group.lessons.length} lezioni`

	const day = start.setLocale("it").toFormat("ccc d LLL")
	return end ? `${day}, ${start.toFormat("HH:mm")} - ${end.toFormat("HH:mm")}` : day
}

// ── REINSERIRE ─────────────────────────────────────────────────────
// Una voce del registro come lezione da rimettere: il modulo della lezione,
// in modalità "reinserisci", parte da qui e chi lo apre controlla e salva.
// Non è un ripristino: la lezione nasce nuova, con i suoi pagamenti e il suo
// evento su Calendar, e nel frattempo il campo può essere stato preso o un
// allievo archiviato — il modulo lo fa vedere prima di salvare.
//
// Una serie torna una serie: dalla prima lezione eliminata, ogni settimana
// fino all'ultima (`reinsertUntil`). Le presenze di una lezione sola restano
// quelle che erano; quelle di una serie no, perché varrebbero per tutte.
// Le voci di prima che il registro tenesse luci, riscaldamento e chi paga il
// campo ripartono da quello che il circolo dice oggi.
export const reinsertLessonData = group => {
	const first = group?.lessons?.[0]
	const last = group?.lessons?.[group.lessons.length - 1]
	if (!first?.lessonStart?.toDate || !first?.lessonEnd?.toDate) return null

	const isSeries = group.lessons.length > 1
	return {
		schoolID: first.schoolID,
		coachID: first.coachID,
		assistantCoachIDs: Array.isArray(first.assistantCoachIDs) ? first.assistantCoachIDs : [],
		lessonStart: first.lessonStart,
		lessonEnd: first.lessonEnd,
		lessonType: first.lessonType,
		sportID: first.sportID,
		court: first.court ?? null,
		usingLights: first.usingLights === true,
		usingHeat: first.usingHeat === true,
		...(first.courtPaidBy ? { courtPaidBy: first.courtPaidBy } : {}),
		...(first.billedTo ? { billedTo: first.billedTo } : {}),
		satLevel: first.satLevel ?? null,
		ptrLevel: first.ptrLevel ?? null,
		lessonNotes: first.lessonNotes ?? "",
		partecipants: (first.partecipants ?? []).map(player => ({
			...player,
			isPresent: isSeries ? true : player.isPresent !== false,
		})),
		reinsertUntil:
			isSeries && last.lessonStart?.toDate
				? DateTime.fromJSDate(last.lessonStart.toDate()).toFormat("yyyy-MM-dd")
				: null,
	}
}
